import React, { useEffect } from 'react'
import { format } from 'date-fns'
import { EditionV4DTO } from '@west-australian-newspapers/publication-types'
import {
    AllEvents,
    createEditionSelectItemEvent,
    createEditionViewItemListEvent,
    formatEditionTitle,
    Product,
    SpecialEditionType,
    WebLink,
} from '@news-mono/web-common'
import { NightlyButton } from '../../../buttons/NightlyButton/NightlyButton'
import {
    StyledNightlyEditionContainer,
    StyledNightlyEditionContent,
    StyledNightlyEditionPicture,
    StyledNightlyEditionHeading,
    StyledNightlyEditionTextContainer,
    StyledNightlyEditionSubHeading,
    StyledNightlyEditionTeaser,
    StyledNightlyEditionButtonContainer,
    Divider,
} from '../SpecificEdition/NightlySpecificEdition.styled'
import { HeaderContainer, HeaderPrefix } from './SpecialEdition.styled'

export interface SpecialEditionProps {
    edition: EditionV4DTO
    heading?: string
    lazyloadImages?: boolean
    shouldShowAllEditions?: boolean
    onEvent?: (event: AllEvents) => void
    allEditionsLink?: string
}

export const SpecialEdition = ({
    edition,
    heading,
    lazyloadImages,
    shouldShowAllEditions = false, // if false, it will show ReadEditionButton instead
    onEvent,
    allEditionsLink = '/on',
}: SpecialEditionProps) => {
    const isTravelEdition = edition.publication === 'the_nightly_travel'
    const editionTypeCapitalized = isTravelEdition ? 'Travel' : 'On'
    const editionTypeLowerCase =
        editionTypeCapitalized.toLowerCase() as SpecialEditionType

    const publicationDate = new Date(edition.publicationDate)
    const normalizedEditionTitle =
        formatEditionTitle(edition.title) ?? 'Edition'
    const title =
        heading ||
        `${normalizedEditionTitle} ${format(publicationDate, 'yyyy')}`
    const subHeading = isTravelEdition
        ? format(publicationDate, 'd MMMM yyyy')
        : 'In this Edition'

    const editionId = edition.id
    const teaser = edition.teaser
    const link = edition.editionWebUrl
    const path = edition.primaryTopic ?? allEditionsLink
    const imageURL = edition.mainImages?.[0]?.src
    const alt = edition.mainImages?.[0]?.captionText

    useEffect(() => {
        if (shouldShowAllEditions) {
            return
        }
        const event = createEditionViewItemListEvent({
            editions: [
                {
                    id: editionId,
                    heading: teaser,
                    title: title,
                },
            ],
            collectionName: `thenightly_${editionTypeLowerCase}_home_page`,
            itemCategory: 'thenightly_specific_edition_page',
        })

        if (onEvent && event) {
            onEvent(event)
        }
    }, [
        shouldShowAllEditions,
        editionId,
        teaser,
        title,
        onEvent,
        editionTypeLowerCase,
    ])

    const handleClick = () => {
        onEvent &&
            onEvent(
                createEditionSelectItemEvent({
                    edition,
                    collectionName: `TheNightly${editionTypeCapitalized}_SpecialEditionImage`,
                    product: Product.TheNightly,
                    itemDetails: {
                        category: shouldShowAllEditions
                            ? `thenightly_${editionTypeLowerCase}_specific_edition_page`
                            : `thenightly_${editionTypeLowerCase}_home_page`,

                        linkText: 'SpecialEditionImageClicked',
                        linkUrl: link,
                        imageUrl: imageURL,
                    },
                }),
            )
    }

    return (
        <>
            <StyledNightlyEditionContainer>
                <StyledNightlyEditionContent>
                    <StyledNightlyEditionPicture>
                        <WebLink to={link} onClick={handleClick}>
                            <img
                                loading={lazyloadImages ? 'lazy' : undefined}
                                src={imageURL ?? ''}
                                onError={({ currentTarget }) => {
                                    currentTarget.onerror = null
                                    currentTarget.src = require('../../ePaper/assets/paper.jpg')
                                }}
                                alt={alt ?? ''}
                            />
                        </WebLink>
                    </StyledNightlyEditionPicture>
                    <HeaderContainer>
                        {!isTravelEdition && (
                            <HeaderPrefix>
                                The Nightly {editionTypeCapitalized}
                            </HeaderPrefix>
                        )}
                        <StyledNightlyEditionHeading
                            as={shouldShowAllEditions ? 'h1' : 'h2'}
                        >
                            {isTravelEdition ? title.toUpperCase() : title}
                        </StyledNightlyEditionHeading>
                        {isTravelEdition && (
                            <HeaderPrefix>By The Nightly</HeaderPrefix>
                        )}
                    </HeaderContainer>
                    <StyledNightlyEditionTextContainer>
                        <StyledNightlyEditionSubHeading>
                            {subHeading}
                        </StyledNightlyEditionSubHeading>
                        <StyledNightlyEditionTeaser>
                            {teaser}
                        </StyledNightlyEditionTeaser>
                    </StyledNightlyEditionTextContainer>
                    <StyledNightlyEditionButtonContainer>
                        <NightlyButton
                            variant={'default'}
                            text={'Read Edition'}
                            action={{
                                type: 'link',
                                to: link,
                                onClick: () =>
                                    onEvent &&
                                    onEvent(
                                        createEditionSelectItemEvent({
                                            edition,
                                            collectionName: `TheNightly${editionTypeCapitalized}_ReadEditionButton`,
                                            product: Product.TheNightly,
                                            itemDetails: {
                                                category: shouldShowAllEditions
                                                    ? `thenightly_${editionTypeLowerCase}_specific_edition_page`
                                                    : `thenightly_${editionTypeLowerCase}_home_page`,

                                                linkText: `Read Edition`,
                                                linkUrl: link,
                                                imageUrl: imageURL,
                                            },
                                        }),
                                    ),
                            }}
                            color={'primary'}
                            fill={'filled'}
                            layout={{
                                shouldGrow: true,
                                alignSelf: 'stretch',
                            }}
                        />
                        {shouldShowAllEditions ? (
                            <NightlyButton
                                variant={'default'}
                                text={'All Editions'}
                                action={{
                                    type: 'link',
                                    to: allEditionsLink,
                                    onClick: () =>
                                        onEvent &&
                                        onEvent(
                                            createEditionSelectItemEvent({
                                                edition,
                                                collectionName: `TheNightly${editionTypeCapitalized}_AllEditionsButton`,
                                                product: Product.TheNightly,
                                                itemDetails: {
                                                    category: `thenightly_${editionTypeLowerCase}_specific_edition_page`,
                                                    linkText: 'All Editions',
                                                    linkUrl: allEditionsLink,
                                                    imageUrl: imageURL,
                                                },
                                            }),
                                        ),
                                }}
                                color={'primary'}
                                fill={'outline'}
                                layout={{
                                    shouldGrow: true,
                                    alignSelf: 'stretch',
                                }}
                            />
                        ) : (
                            <NightlyButton
                                variant={'default'}
                                text={'More Articles'}
                                action={{
                                    type: 'link',
                                    to: path,
                                    onClick: () =>
                                        onEvent &&
                                        onEvent(
                                            createEditionSelectItemEvent({
                                                edition,
                                                collectionName: `TheNightly${editionTypeCapitalized}_MoreArticlesButton`,
                                                product: Product.TheNightly,
                                                itemDetails: {
                                                    category: `thenightly_${editionTypeLowerCase}_home_page`,
                                                    linkText: 'More Articles',
                                                    linkUrl: path,
                                                    imageUrl: imageURL,
                                                },
                                            }),
                                        ),
                                }}
                                color={'primary'}
                                fill={'outline'}
                                layout={{
                                    shouldGrow: true,
                                    alignSelf: 'stretch',
                                }}
                            />
                        )}
                    </StyledNightlyEditionButtonContainer>
                    <StyledNightlyEditionTextContainer></StyledNightlyEditionTextContainer>
                </StyledNightlyEditionContent>
            </StyledNightlyEditionContainer>

            <Divider />
        </>
    )
}
